// File: src/pages/seller/AddProduct/sections/BasicDetails.jsx
import React, { useEffect, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  CRow,
  CCol,
  CFormLabel,
  CFormSelect,
  CFormInput,
  CButton,
} from "@coreui/react";
import { useParams } from "react-router-dom";
import { loadGoogleMaps } from "../../../../utils/loadGoogleMaps";
import CategoryRequestModal from "../modals/CategoryRequestModal";

const BasicDetails = ({ categories = [], subCategories = [], setValue }) => {
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize Google Places Autocomplete (Worldwide)
  useEffect(() => {
    if (isEditMode) return;
    if (!inputRef.current) return;

    loadGoogleMaps().then((google) => {
      const autocomplete = new google.maps.places.Autocomplete(
        inputRef.current,
        { types: ["geocode"] },
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place?.formatted_address && place.geometry) {
          setValue("location", place.formatted_address, {
            shouldValidate: true,
          });
          setValue("seller_latitude", place.geometry.location.lat(), {
            shouldDirty: true,
          });
          setValue("seller_longitude", place.geometry.location.lng(), {
            shouldDirty: true,
          });
        }
      });

      autocompleteRef.current = autocomplete;
    });
  }, [setValue, isEditMode]);

  // Get Current Location (Geolocation API)
  const handleUseCurrentLocation = () => {
    if (!window.google?.maps) {
      alert("Google Maps not loaded yet.");
      return;
    }
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        setValue("seller_latitude", latitude, { shouldDirty: true });
        setValue("seller_longitude", longitude, { shouldDirty: true });

        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            if (status === "OK" && results[0]) {
              const address = results[0].formatted_address;
              setValue("location", address);
            } else {
              alert(
                "Unable to retrieve your address. Please type it manually.",
              );
            }
          },
        );
      },
      (error) => {
        console.error("Error getting location:", error);
        alert(
          "Unable to access your location. Please enable location permissions.",
        );
      },
    );
  };

  return (
    <>
      <div
        style={{
          border: "1px solid #dcdcdc",
          borderRadius: "8px",
          padding: "1.5rem",
          position: "relative",
          marginBottom: "1.5rem",
        }}
      >
        {/* Section Title that overlaps the border */}
        <span
          style={{
            position: "absolute",
            top: "-0.8rem",
            left: "1rem",
            backgroundColor: "white",
            padding: "0 0.5rem",
            fontWeight: "600",
            color: "#333",
            fontSize: "1rem",
          }}
        >
          Basic Details
        </span>

        {/* Category + Subcategory + Metal Code */}
        <CRow className="mb-3 align-items-end">
          <CCol md={3}>
            <CFormLabel>Category</CFormLabel>
            <Controller
              name="category_id"
              control={control}
              render={({ field }) => (
                <CFormSelect
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue("subcategory_id", "");
                    setValue("metal_code", "");
                  }}
                  disabled={isEditMode}
                  options={[
                    { label: "Select", value: "" },
                    ...categories.map((c) => ({
                      label: c.name,
                      value: String(c.id),
                    })),
                  ]}
                />
              )}
            />
            {errors.category_id && (
              <div style={{ color: "red" }}>{errors.category_id.message}</div>
            )}
          </CCol>

          <CCol md={3}>
            <CFormLabel>Subcategory</CFormLabel>
            <Controller
              name="subcategory_id"
              control={control}
              render={({ field }) => (
                <CFormSelect
                  {...field}
                  disabled={isEditMode}
                  options={[
                    { label: "Select", value: "" },
                    ...subCategories.map((s) => ({
                      label: s.name,
                      value: String(s.id),
                    })),
                  ]}
                />
              )}
            />
            {errors.subcategory_id && (
              <div style={{ color: "red" }}>
                {errors.subcategory_id.message}
              </div>
            )}
          </CCol>

          <CCol md={3}>
            <CFormLabel>Metal Code</CFormLabel>
            <Controller
              name="metal_code"
              control={control}
              render={({ field }) => <CFormInput {...field} />}
              disabled
            />
          </CCol>
{/* 
          <CCol md={3}>
            <CButton
              color="primary"
              disabled={isEditMode}
              onClick={() => setCategoryModalVisible(true)}
            >
              Request New Category
            </CButton>
          </CCol> */}
        </CRow>

        {/* Location (Full width + Current Location button) */}
        <CRow className="mb-3 align-items-end">
          <CCol md={8}>
            <CFormLabel>Location</CFormLabel>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <CFormInput
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue("seller_latitude", null);
                    setValue("seller_longitude", null);
                  }}
                  ref={(el) => {
                    field.ref(el);
                    inputRef.current = el;
                  }}
                  placeholder="Start typing to search or use current location"
                  disabled={isEditMode}
                />
              )}
            />
            {errors.location && (
              <div style={{ color: "red" }}>{errors.location.message}</div>
            )}
          </CCol>
          <CCol md={4} className="text-end">
            <CButton
              color="primary"
              disabled={isEditMode}
              onClick={handleUseCurrentLocation}
            >
              Use Current Location
            </CButton>
          </CCol>
        </CRow>
      </div>

      {/* Category Request Modal */}
      <CategoryRequestModal
        visible={categoryModalVisible}
        onClose={() => setCategoryModalVisible(false)}
      />
    </>
  );
};

export default React.memo(BasicDetails);
